import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Lightning,
  CurrencyDollar,
  Truck,
  TrendUp,
  Trophy,
  ArrowLeft,
  ChartBar,
  Coins,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

function fmt(n, currency) {
  return `${currency || "JMD"} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MonthName(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "short", year: "2-digit" });
}

function KPICard({ label, value, currency, sublabel, accent }) {
  const palette = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-900",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    zinc: "bg-white border-zinc-200 text-zinc-900",
  };
  const cls = accent ? `bg-gradient-to-br ${palette[accent]}` : palette.zinc;
  return (
    <div className={`rounded-md border p-5 ${cls}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold" style={{ fontFamily: "Work Sans" }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <span className="text-[11px] font-mono text-zinc-500 mr-1">{currency || "JMD"}</span>
        {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
      {sublabel && <div className="text-[11px] text-zinc-600 mt-1.5">{sublabel}</div>}
    </div>
  );
}

export default function ZipExpressRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = () =>
    api.get("/zipexpress/revenue")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load revenue report"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const runPayout = async () => {
    if (!data?.unpaid_balance) return;
    if (!confirm(`Run payout batch for ${data.lifetime.currency} ${data.unpaid_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${data.unpaid_count} deliveries)?`)) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/zipexpress/payouts/run", {});
      toast.success(`Payout batch issued — ${r.marked} deliveries marked paid`);
      setLoading(true);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setRunning(false); }
  };

  if (loading) return <div className="p-8 text-zinc-500" data-testid="zipx-rev-loading">Loading…</div>;
  if (!data) return <div className="p-8 text-zinc-500">No data</div>;

  const maxMonth = Math.max(1, ...(data.monthly || []).map((m) => m.total_fee || 0));
  const currency = data.lifetime?.currency || "JMD";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="zipx-revenue-page">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            ZipExpress · Last-Mile Network
          </div>
          <h1
            className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100"
            style={{ fontFamily: "Work Sans" }}
          >
            Revenue Report
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Aggregate earnings across the entire ZipExpress driver network. Calculated at the moment each ground delivery is marked delivered.
          </p>
        </div>
        <Link to="/zipexpress">
          <Button variant="outline" data-testid="zipx-rev-back-btn">
            <ArrowLeft size={14} className="mr-1.5" /> Back to settings
          </Button>
        </Link>
      </div>

      {/* Pending payout banner */}
      {data.unpaid_balance > 0 && (
        <div
          className="mb-6 rounded-md border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-100 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          data-testid="zipx-rev-payout-banner"
        >
          <div className="min-w-0 flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-white/70 border border-amber-200 flex items-center justify-center shrink-0">
              <Coins size={24} weight="duotone" className="text-amber-600" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-800">
                Unpaid driver balance
              </div>
              <div className="mt-1 text-2xl font-semibold text-amber-900" style={{ fontFamily: "Work Sans" }} data-testid="zipx-rev-unpaid">
                <span className="text-[12px] font-mono text-amber-700 mr-1">{currency}</span>
                {Number(data.unpaid_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                {data.unpaid_count} unpaid {data.unpaid_count === 1 ? "delivery" : "deliveries"} · next scheduled payout{" "}
                <b>{new Date(data.next_payout_date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</b>
              </div>
            </div>
          </div>
          <Button
            onClick={runPayout}
            disabled={running}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="zipx-run-payout-btn"
          >
            {running ? "Running..." : <><Coins size={15} weight="bold" className="mr-1.5" /> Run payout batch now</>}
          </Button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPICard
          label="Lifetime revenue"
          value={data.lifetime.total_fee}
          currency={currency}
          sublabel={`${data.lifetime.deliveries} deliveries · all-time`}
          accent="blue"
        />
        <KPICard
          label="ZipExpress cut"
          value={data.lifetime.zipexpress_total}
          currency={currency}
          sublabel="Network commission (lifetime)"
          accent="amber"
        />
        <KPICard
          label="Drivers paid"
          value={data.lifetime.driver_total}
          currency={currency}
          sublabel={`Paid out to drivers (lifetime)`}
          accent="emerald"
        />
        <KPICard
          label="This month"
          value={data.this_month.total_fee}
          currency={currency}
          sublabel={`${data.this_month.deliveries} deliveries this month`}
        />
      </div>

      {/* Monthly trend */}
      <div className="bg-white border border-zinc-200 rounded-md p-5 mb-6" data-testid="zipx-rev-monthly">
        <div className="flex items-center gap-2 mb-4">
          <ChartBar size={16} weight="bold" className="text-zinc-700" />
          <h2 className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>
            Monthly trend
          </h2>
          <span className="text-[11px] text-zinc-500 ml-1">last 12 months</span>
        </div>
        {data.monthly.length === 0 ? (
          <div className="text-sm text-zinc-500 py-8 text-center">No revenue logged yet.</div>
        ) : (
          <div className="space-y-2.5">
            {data.monthly.map((m) => (
              <div key={m.month} className="flex items-center gap-3" data-testid={`zipx-rev-month-${m.month}`}>
                <div className="text-xs text-zinc-600 font-mono w-16 shrink-0">{MonthName(m.month)}</div>
                <div className="flex-1 bg-zinc-100 rounded h-8 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#002BF6] to-blue-400 h-full rounded transition-all"
                    style={{ width: `${Math.max(2, (m.total_fee / maxMonth) * 100)}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-3 justify-between">
                    <span className="text-[11px] font-semibold text-white drop-shadow">{fmt(m.total_fee, m.currency)}</span>
                    <span className="text-[10px] text-zinc-700 font-mono">{m.deliveries} pkg</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top drivers */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="zipx-rev-top-drivers">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
          <Trophy size={16} weight="bold" className="text-amber-600" />
          <h2 className="text-base font-semibold text-zinc-900" style={{ fontFamily: "Work Sans" }}>
            Top earning drivers
          </h2>
          <span className="text-[11px] text-zinc-500 ml-1">top 10 by driver payout</span>
        </div>
        {data.top_drivers.length === 0 ? (
          <div className="text-sm text-zinc-500 py-12 text-center">No driver earnings yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-5 py-3 font-semibold w-12">#</th>
                <th className="px-5 py-3 font-semibold">Driver</th>
                <th className="px-5 py-3 font-semibold text-right">Deliveries</th>
                <th className="px-5 py-3 font-semibold text-right">Driver payout</th>
                <th className="px-5 py-3 font-semibold text-right">Total billed</th>
              </tr>
            </thead>
            <tbody>
              {data.top_drivers.map((d, i) => (
                <tr key={d.driver_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`zipx-rev-driver-${d.driver_id}`}>
                  <td className="px-5 py-3 text-zinc-500 font-mono">{i + 1}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-zinc-900">{d.driver_name || "—"}</div>
                    <div className="text-[10px] text-zinc-400 font-mono">{d.driver_id?.slice(0, 8)}</div>
                  </td>
                  <td className="px-5 py-3 text-right text-zinc-700 font-mono">{d.deliveries}</td>
                  <td className="px-5 py-3 text-right">
                    <span className="font-semibold text-emerald-700">{fmt(d.driver_total, d.currency)}</span>
                  </td>
                  <td className="px-5 py-3 text-right text-zinc-600">{fmt(d.total_fee, d.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
